import type { CustomControlCode } from '@common/enums';
import type { DeepPickPath } from '@common/utils/deepPickUtils';
import { nonNullable } from '@common/utils/nonNullableTypeUtils';
import type {
    ContentField,
    Field,
    FieldType,
    InputField,
    InputFieldWithSectionAndSubsection,
    OptionsField,
    QuantityInputField,
    Section,
    Subsection,
} from './formEngine.types';
import type { TypedReadSchema, TypedReadSchemaItem } from './typedReadSchema.types';

export function isContentField<
    Data extends Record<string, unknown>,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
    Key extends string,
>(
    item: InputField<Data, TCustomControlCode, Version> | ContentField<Key, Data, TCustomControlCode, Version>
): item is ContentField<Key, Data, TCustomControlCode, Version> {
    return item.type === 'content';
}

export function isInputField<
    Data extends Record<string, unknown>,
    Key extends string,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>(
    item: InputField<Data, TCustomControlCode, Version> | ContentField<Key, Data, TCustomControlCode, Version>
): item is InputField<Data, TCustomControlCode, Version> {
    return item.type !== 'content';
}

export function isOptionsField<
    Data extends Record<string, unknown>,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>(
    field: InputField<Data, TCustomControlCode, Version>
): field is InputField<Data, TCustomControlCode, Version> & OptionsField<Data, TCustomControlCode, Version> {
    return field.type === 'select' || field.type === 'radioGroup' || field.type === 'checkboxGroup';
}

export function isQuantityInputField<
    Data extends Record<string, unknown>,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>(
    field: InputField<Data, TCustomControlCode, Version>
): field is QuantityInputField<Data, TCustomControlCode, Version> {
    return field.type === 'quantity';
}

export function getInputFieldsFromSubsection<
    Data extends Record<string, unknown>,
    ContentKey extends string | undefined,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>(
    subsection: Subsection<Data, ContentKey, TCustomControlCode, Version>
): InputField<Data, TCustomControlCode, Version>[] {
    return subsection.fields.filter((field) => isInputField(field));
}

export function getInputFieldsFromSection<
    Data extends Record<string, unknown>,
    ContentKey extends string | undefined,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>(
    section: Section<Data, ContentKey, TCustomControlCode, Version>
): InputFieldWithSectionAndSubsection<Data, ContentKey, TCustomControlCode, Version>[] {
    const sectionFields = section.fields.filter((field) => isInputField(field)).map((field) => ({ ...field, section }));

    const subsectionFields =
        section.subsections?.flatMap((subsection) =>
            getInputFieldsFromSubsection(subsection).map((field) => ({
                ...field,
                section,
                subsection,
            }))
        ) ?? [];

    return [...sectionFields, ...subsectionFields];
}

export function getInputFieldsFromSections<
    Data extends Record<string, unknown>,
    ContentKey extends string | undefined,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>(
    sections: Section<Data, ContentKey, TCustomControlCode, Version>[]
): InputFieldWithSectionAndSubsection<Data, ContentKey, TCustomControlCode, Version>[] {
    return sections.flatMap((section) => getInputFieldsFromSection(section));
}

export function getInputField<
    Data extends Record<string, unknown>,
    Key extends DeepPickPath<Data>,
    ContentKey extends string | undefined,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>(
    sections: Section<Data, ContentKey, TCustomControlCode, Version>[],
    key: Key
): InputFieldWithSectionAndSubsection<Data, ContentKey, TCustomControlCode, Version> {
    const field = getInputFieldsFromSections(sections).find((fieldElement) => fieldElement.key === key);

    return nonNullable(field);
}

export function getAssetsKeyOrFieldKey(fieldType: unknown, fieldKey: string): string {
    return fieldType === 'assets' ? 'assets' : fieldKey;
}

/*
 * Everything below this line helps to convert the new setup into the old setup.
 */
function convertToReadSectionType(type: FieldType): TypedReadSchemaItem['type'] {
    switch (type) {
        case 'assets':
        case 'boolean':
        case 'icd10':
        case 'monthInput':
        case 'number':
        case 'quantity':
            return type;
        case 'dateInput':
            return 'dateAsString';
        default:
            return undefined;
    }
}

function convertToReadSectionItem<
    Data extends Record<string, unknown>,
    ContentKey extends string | undefined,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>(field: Field<Data, ContentKey, TCustomControlCode, Version>): TypedReadSchemaItem {
    return isInputField(field)
        ? {
              append: field.type === 'number' ? field.append : undefined,
              key: field.key,
              label: field.label,
              type: convertToReadSectionType(field.type),
          }
        : {
              key: field.key,
              label: '',
          };
}

export function convertToReadSections<
    Data extends Record<string, unknown>,
    ContentKey extends string | undefined = undefined,
    TCustomControlCode extends CustomControlCode | undefined = undefined,
    Version extends number = 1,
>(sections: Section<Data, ContentKey, TCustomControlCode, Version>[]): TypedReadSchema<keyof Data>[] {
    return sections.map((section) => ({
        items: section.fields.map(convertToReadSectionItem),
        sectionTitle: section.title,
        subsections: section.subsections?.map((subsection) => ({
            items: subsection.fields.map(convertToReadSectionItem),
            title: subsection.title,
        })),
    }));
}
